import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaCalendarAlt } from 'react-icons/fa';
import { PiChartBar } from 'react-icons/pi';
import moment from 'moment';
import DatePicker, {
  CalendarContainer,
  registerLocale,
} from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';

import 'react-datepicker/dist/react-datepicker.css';
import DatesGraphWithPrice from './DatesGraphWithPrice/DatesGraphWithPrice';

function addDays(date, days) {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + days);
  return newDate;
}

const today = new Date();
let date_from = addDays(today, 5);
let date_to = addDays(today, 16);

const defaultDates = (from, to) => {
  return !(
    date_from === from &&
    date_to === to &&
    window.location.pathname !== '/'
  )
    ? false
    : true;
};

function Dates({
  datefrom,
  dateto,
  departure,
  currency,
  country,
  onChoose,
  hideregular,
  onChangeRegular,
  is_mobile,
}) {
  const [wasChanged, setWasChanged] = useState(
    defaultDates(date_from, date_to)
  );
  const [datesWithPrice, setDatesWithPrice] = useState({});
  const [dateRange, setDateRange] = useState([
    moment(datefrom, 'DD.MM.YYYY').toDate(),
    moment(dateto, 'DD.MM.YYYY').toDate(),
  ]);
  const [isCheckboxDisabled, setCheckboxDisabled] = useState(false);
  const [isGraphOpened, setGraphOpened] = useState(false);
  const [startDate, endDate] = dateRange;
  const [rawPriceData, setRawPriceData] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setGraphOpened(false);
      }
      if (e.key === 'Escape') {
        setGraphOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleClickOutside);
    };
  }, []);

  registerLocale('ru', ru);

  function onDatesChoose(dates) {
    setDateRange(dates);
    if (dates[1] !== null) {
      onChoose({
        datefrom: dates[0].toLocaleDateString('ru'),
        dateto: dates[1].toLocaleDateString('ru'),
      });
      setWasChanged(true);
    }
  }

  function handlePriceGraphOpened() {
    setGraphOpened(!isGraphOpened);
  }

  const renderDayContents = (day, date) => {
    const twoDigitMonth = (date.getMonth() + 1).toString().padStart(2, '0');
    const twoDigitDay = day.toString().padStart(2, '0');
    const normalizedDate = `${date.getFullYear()}-${twoDigitMonth}-${twoDigitDay}`;

    if (datesWithPrice[normalizedDate]) {
      return (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <p>{date.getDate()}</p>
          <p className="datepicker__day-price">
            {datesWithPrice[normalizedDate]}
          </p>
        </span>
      );
    } else {
      return (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <p>{date.getDate()}</p>
          <p className="datepicker__day-noprice"></p>
        </span>
      );
    }
  };

  const fetchCalendarData = useCallback(async () => {
    setCheckboxDisabled(true);
    const currentMonth = moment().month() + 1;
    const url = `https://www.geograftour.com/tourvisor/get-dates/?departure=${departure}&currency=${currency}&country=${country}&month=${currentMonth},${
      currentMonth + 1
    },${currentMonth + 2},${currentMonth + 3}&regular=${hideregular ? 1 : 0}`;

    try {
      const response = await fetch(url);
      const res = await response.json();

      if (res?.data?.calendar?.month) {
        const monthData = res.data.calendar.month;
        setRawPriceData(monthData);
        const newDayPrices = {};

        for (let monthKey in monthData) {
          const daysList = monthData[monthKey].days;

          if (daysList && Array.isArray(daysList)) {
            daysList.forEach((day) => {
              const dayFormatted = moment(day.date, 'DD.MM.YYYY').format(
                'YYYY-MM-DD'
              );
              if (!moment().subtract(1, 'days').isSameOrAfter(dayFormatted)) {
                newDayPrices[dayFormatted] = day.price;
              }
            });
          }
        }

        setDatesWithPrice(newDayPrices);
        setCheckboxDisabled(false);
      }
    } catch (err) {
      console.error('Error fetching calendar data:', err);
    }
  }, [departure, currency, country, hideregular]);

  useEffect(() => {
    fetchCalendarData();
  }, [hideregular, country, departure, currency]);

  useEffect(() => {
    setDateRange([
      moment(datefrom, 'DD.MM.YYYY').toDate(),
      moment(dateto, 'DD.MM.YYYY').toDate(),
    ]);
  }, [datefrom, dateto]);

  const calendarContainer = ({ className, children }) => {
    return (
      <CalendarContainer className={className}>
        {children}
        <label className="hidecharter">
          <input
            onChange={(e) => {
              onChangeRegular(e.target.checked);
            }}
            type="checkbox"
            className="hidecharter__checkbox"
            checked={hideregular}
            disabled={isCheckboxDisabled}
          />
          Показывать только чартерные рейсы
        </label>
      </CalendarContainer>
    );
  };

  return (
    <div className={`search-widget dates`}>
      <div className={`search-field ${wasChanged}`}>
        <FaCalendarAlt className="search-preview-label-icon" />
        <div className="dates-label">ДАТЫ ВЫЛЕТА</div>
        <PiChartBar
          className="dates-chosen"
          onClick={handlePriceGraphOpened}
          title="График цен по дням"
        />
        {isGraphOpened ? (
          <DatesGraphWithPrice
            dropdownRef={dropdownRef}
            rawPriceData={rawPriceData}
            isOpened={isGraphOpened}
            departCity={departure}
            currency={currency}
            country={country}
            flightTypeCharter={hideregular}
            onChangeFlightType={onChangeRegular}
            dateFrom={datefrom}
            dateTo={dateto}
            handleDateChoose={onChoose}
            handlePriceGraphOpened={handlePriceGraphOpened}
          />
        ) : null}
        <DatePicker
          selectsRange={true}
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            onDatesChoose(update);
          }}
          dateFormat="dd.MM"
          monthsShown={!is_mobile ? 2 : 1}
          minDate={new Date()}
          locale="ru"
          className="datepicker__input"
          calendarClassName="datepicker__header"
          renderDayContents={renderDayContents}
          calendarContainer={calendarContainer}
          withPortal={is_mobile}
        />
      </div>
    </div>
  );
}

export default Dates;
